import type { ObjectId } from '@racemap/sdk/schema/base';
import { USER_ROLE } from '@racemap/utilities/consts/trackers';
import { getUserRoleForTracker } from '@racemap/utilities/functions/trackers';
import { isDefined } from '@racemap/utilities/functions/validation';
import type { TrackerObject } from '@racemap/utilities/types/trackers';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Button, Flex, Form, Popconfirm, Select, Space } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCurrentUser, useIsAdmin } from '../../lib/customHooks';
import { useStore } from '../../store/reducers';
import { DateRangePicker } from '../BasicComponents/DateRangePicker';
import { UserChooser } from '../BasicComponents/UserChooser/UserChooser';
import { IconPlus, IconTrash } from '../Icon';

interface Props {
  trackerIds: Array<ObjectId>;
  onCancel?: () => void;
}

interface FieldType {
  userId: ObjectId;
  role: USER_ROLE;
  borrowingPeriod?: [DateTime, DateTime];
}

export function AddUserToTrackers({ trackerIds, onCancel }: Props) {
  const [form] = Form.useForm<FieldType>();
  const isAdmin = useIsAdmin();
  const trackers = useStore((s) => s.trackers.items);
  const user = useCurrentUser();
  const { addUserToTracker, removeBorrowersAndEditorsFromTracker } = useStore(
    (s) => s.trackers.actions,
  );

  const role = Form.useWatch('role', form);
  const targetTrackers = trackerIds.map((id) => trackers.get(id.toHexString())).filter(isDefined);
  const userRole = useMemo(() => userMinorRole(targetTrackers, user), [trackers, trackerIds, user]);

  const isEditor = userRole === USER_ROLE.EDITOR;
  const isBorrower = userRole === USER_ROLE.BORROWER;

  useEffect(() => {
    form.setFieldsValue({
      role: getDefaultRole(userRole),
    });
  }, [userRole]);

  const handleAdd = async (): Promise<void> => {
    const { userId, role, borrowingPeriod = [] } = form.getFieldsValue();
    const [startTime, endTime] = borrowingPeriod;

    await addUserToTracker({
      trackerIds,
      userId,
      role,
      startTime,
      endTime,
    });

    onCancel?.();
  };

  const clearTrackerUserList = async () => {
    for (const trackerId of trackerIds) {
      await removeBorrowersAndEditorsFromTracker(trackerId);
    }
    toast.success('Remove all users from all trackers!');
  };

  return (
    <Form name="addUserToTrackers" onFinish={handleAdd} layout="vertical" form={form}>
      <Form.Item
        name="userId"
        label="User"
        rules={[{ required: true, message: 'Please choose a user!' }]}
      >
        <UserChooser />
      </Form.Item>

      <Form.Item
        label="Role"
        name="role"
        rules={[{ required: true, message: 'Please choose a valid role!' }]}
      >
        <Select>
          {isAdmin && <Select.Option value={USER_ROLE.OWNER}>Owner</Select.Option>}
          <Select.Option disabled={isEditor || isBorrower} value={USER_ROLE.EDITOR}>
            Editor
          </Select.Option>
          <Select.Option disabled={isBorrower} value={USER_ROLE.BORROWER}>
            Borrower
          </Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Borrowing Period"
        name="borrowingPeriod"
        hidden={role !== USER_ROLE.BORROWER}
        rules={[
          { required: role === USER_ROLE.BORROWER, message: 'Please choose a borrowing period!' },
        ]}
      >
        <DateRangePicker
          format="yyyy-MM-dd"
          style={{ width: '100%' }}
          minDate={DateTime.now().startOf('day')}
        />
      </Form.Item>

      <Flex justify="space-between">
        <Popconfirm
          onConfirm={clearTrackerUserList}
          title={
            'Do you really want to clear the user history of that tracker. That removes all borrowers and editors!'
          }
        >
          <Button danger icon={<IconTrash />}>
            Clear user history
          </Button>
        </Popconfirm>

        <Space>
          {onCancel != null && <Button onClick={onCancel}>Cancel</Button>}
          <Button
            icon={<IconPlus />}
            type="primary"
            // actually that shouldn't be necessary, but if the form is a modal, it is
            onClick={() => form.submit()}
          >
            Add
          </Button>
        </Space>
      </Flex>
    </Form>
  );
}

const ROLE_RANKING: Record<USER_ROLE, number> = {
  [USER_ROLE.OWNER]: 3,
  [USER_ROLE.EDITOR]: 2,
  [USER_ROLE.BORROWER]: 1,
};

function userMinorRole(
  trackers: Immutable<Array<TrackerObject>>,
  user: Immutable<User_Legacy> | null,
): USER_ROLE {
  let role = USER_ROLE.OWNER;
  if (user == null) return USER_ROLE.BORROWER;

  for (const tracker of trackers) {
    if (tracker == null) continue;
    const userRoleForTracker = getUserRoleForTracker(tracker, user.id);
    if (userRoleForTracker == null) continue;
    if (ROLE_RANKING[role] > ROLE_RANKING[userRoleForTracker]) role = userRoleForTracker;
  }

  return role || USER_ROLE.BORROWER;
}

function getDefaultRole(userRole: USER_ROLE): USER_ROLE {
  if (userRole === USER_ROLE.OWNER) return USER_ROLE.EDITOR;
  if (userRole === USER_ROLE.EDITOR) return USER_ROLE.BORROWER;
  if (userRole === USER_ROLE.BORROWER) return USER_ROLE.BORROWER;
  return USER_ROLE.OWNER;
}
